import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  TrendingUp, Globe, Clock, MousePointerClick, Eye, ScrollText, Sparkles,
  RefreshCw, Loader2, Target, AlertTriangle, ChevronDown, ChevronUp, ExternalLink, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const SOURCE_LABEL = (src) => {
  if (!src) return 'unknown';
  if (src === 'direct') return 'Direct';
  if (src === 'search') return 'Search';
  if (src === 'social') return 'Social';
  if (src.startsWith('utm:')) return 'Campaign · ' + src.split(':')[1];
  if (src.startsWith('referral:')) return 'Referral · ' + src.split(':')[1];
  return src;
};

const SOURCE_COLOR = (src) => {
  if (src === 'direct') return { bg: '#eff4ff', text: '#004ac6' };
  if (src === 'search') return { bg: '#ecfdf5', text: '#047857' };
  if (src === 'social') return { bg: '#fdf2f8', text: '#be185d' };
  if (src?.startsWith('utm:')) return { bg: '#fef3c7', text: '#92400e' };
  return { bg: '#f1f5f9', text: '#475569' };
};

// Convert a 2-letter ISO country code to its flag emoji (regional indicator pair).
const FLAG = (cc) => {
  if (!cc || cc.length !== 2) return '🌐';
  const A = 0x1F1E6;
  return String.fromCodePoint(A + cc.toUpperCase().charCodeAt(0) - 65)
       + String.fromCodePoint(A + cc.toUpperCase().charCodeAt(1) - 65);
};

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-[#c3c6d7]/30 p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-[#004ac6]" />
        <span className="text-[10px] font-black uppercase tracking-widest text-[#737686]">{label}</span>
      </div>
      <div className="text-2xl font-extrabold text-[#0b1c30] tracking-tight">{value}</div>
      {sub && <div className="text-[10px] text-[#737686] mt-0.5">{sub}</div>}
    </div>
  );
}

function VerdictPill({ v }) {
  if (!v) return <span className="text-[10px] font-bold text-[#737686]">—</span>;
  const buy = v.will_buy_pct ?? 0;
  const ret = v.will_return_pct ?? 0;
  const tone =
    buy >= 60 ? 'bg-emerald-50 text-emerald-700' :
    buy >= 30 ? 'bg-amber-50 text-amber-700' :
                'bg-slate-50 text-slate-600';
  return (
    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${tone}`}>
      buy {buy}% · return {ret}%
    </span>
  );
}

export default function AdminTrafficPage() {
  const [data, setData] = useState({ items: [], total: 0, rollup: null });
  const [loading, setLoading] = useState(true);
  const [filterSrc, setFilterSrc] = useState('');
  const [analyzing, setAnalyzing] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSrc) params.set('source', filterSrc);
      params.set('limit', '200');
      const { data } = await axios.get(`${API}/admin/analytics/sessions?${params.toString()}`, { headers: auth() });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load analytics');
    } finally {
      setLoading(false);
    }
  }, [filterSrc]);

  useEffect(() => { load(); }, [load]);

  const analyze = async (sid) => {
    setAnalyzing(sid);
    try {
      const { data } = await axios.post(`${API}/admin/analytics/sessions/${sid}/analyze`, {}, { headers: auth() });
      toast.success(`AI verdict: ${data?.verdict?.persona || 'unknown'} · ${data?.verdict?.engagement || ''}`);
      await load();
      setExpanded(s => ({ ...s, [sid]: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(null);
    }
  };

  const toggle = (id) => setExpanded(s => ({ ...s, [id]: !s[id] }));

  const r = data.rollup || {};
  const totalMin = Math.round((r.total_minutes || 0));
  const avgScroll = Math.round(r.avg_scroll || 0);

  // Derive top-source breakdown from the loaded items (for the filter chips).
  const sources = Array.from(new Set((data.items || []).map(i => i.source))).slice(0, 6);

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="admin-traffic-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.25em] text-[#737686] flex items-center gap-2">
            <TrendingUp className="w-3 h-3 text-emerald-600" /> ADMIN · TRAFFIC ANALYTICS
          </div>
          <h1 className="text-3xl font-extrabold text-[#0b1c30] tracking-tight mt-1">
            Traffic <span className="bg-gradient-to-r from-[#004ac6] to-[#7c3aed] bg-clip-text text-transparent">Intelligence</span>
          </h1>
          <p className="text-sm text-[#434655] mt-1">Where your visitors come from, how deep they engage, and AI-scored buying intent.</p>
        </div>
        <button
          onClick={load}
          data-testid="traffic-refresh"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-[#0b1c30] bg-white border border-[#c3c6d7]/40 hover:bg-[#eff4ff] transition-all">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard icon={Eye} label="Sessions" value={data.total || 0} sub={`${data.items?.length || 0} loaded`} />
        <StatCard icon={Clock} label="Total minutes" value={totalMin} sub="across all visits" />
        <StatCard icon={ScrollText} label="Avg scroll" value={`${avgScroll}%`} />
        <StatCard icon={MousePointerClick} label="Outbound clicks" value={r.total_clicks || 0} />
        <StatCard icon={Sparkles} label="AI-analysed" value={r.with_ai || 0} sub={`${data.total ? Math.round(((r.with_ai||0) / data.total) * 100) : 0}% coverage`} />
      </div>

      {/* Source filter chips */}
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="traffic-source-filter">
        <span className="text-[10px] font-black uppercase tracking-widest text-[#737686]">Source:</span>
        <button
          onClick={() => setFilterSrc('')}
          className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all ${!filterSrc ? 'bg-[#004ac6] text-white' : 'bg-white text-[#434655] border border-[#c3c6d7]/40 hover:bg-[#eff4ff]'}`}>
          All
        </button>
        {sources.map(s => {
          const c = SOURCE_COLOR(s);
          const active = filterSrc === s;
          return (
            <button
              key={s}
              onClick={() => setFilterSrc(active ? '' : s)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all`}
              style={active
                ? { backgroundColor: c.text, color: '#fff' }
                : { backgroundColor: c.bg, color: c.text }}>
              {SOURCE_LABEL(s)}
            </button>
          );
        })}
      </div>

      {/* Top-countries strip */}
      {(data.top_countries || []).length > 0 && (
        <div className="bg-white rounded-xl border border-[#c3c6d7]/30 p-3 mb-5 shadow-sm" data-testid="traffic-top-countries">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-3.5 h-3.5 text-[#004ac6]" />
            <span className="text-[10px] font-black uppercase tracking-widest text-[#737686]">Top countries</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(data.top_countries || []).map(c => (
              <div
                key={c.country_code}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#f8f9ff] text-[#0b1c30] text-[12px] font-bold border border-[#c3c6d7]/30"
                data-testid={`country-pill-${c.country_code}`}>
                <span className="text-base leading-none">{FLAG(c.country_code)}</span>
                <span>{c.country_code}</span>
                <span className="text-[10px] text-[#737686] font-mono ml-1">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sessions table */}
      <div className="bg-white rounded-xl border border-[#c3c6d7]/30 shadow-sm overflow-hidden">
        <div className="hidden lg:grid grid-cols-12 gap-2 px-4 py-2 bg-[#f8f9ff] text-[9px] font-black uppercase tracking-widest text-[#737686] border-b border-[#c3c6d7]/30">
          <div className="col-span-3">Session · Source</div>
          <div className="col-span-1">Country</div>
          <div className="col-span-2">Started</div>
          <div className="col-span-1 text-right">Pages</div>
          <div className="col-span-1 text-right">Scroll</div>
          <div className="col-span-1 text-right">Mins</div>
          <div className="col-span-3 text-right">AI Verdict</div>
        </div>
        {loading && (
          <div className="p-8 text-center text-sm text-[#737686] flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading sessions…
          </div>
        )}
        {!loading && (data.items || []).length === 0 && (
          <div className="p-12 text-center text-sm text-[#737686]">
            <Globe className="w-10 h-10 text-[#c3c6d7] mx-auto mb-3" />
            No sessions match this filter yet. Once a visitor consents to analytics, their activity shows up here.
          </div>
        )}
        {!loading && (data.items || []).map(s => {
          const c = SOURCE_COLOR(s.source);
          const isOpen = !!expanded[s.id];
          return (
            <div key={s.id} className="border-b border-[#c3c6d7]/20 last:border-0" data-testid={`session-row-${s.id}`}>
              <div className="grid grid-cols-2 lg:grid-cols-12 gap-2 px-4 py-3 hover:bg-[#f8f9ff]/60 transition-colors">
                <div className="lg:col-span-3 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: c.bg, color: c.text }}>
                      {SOURCE_LABEL(s.source)}
                    </span>
                    {s.user_id && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">AUTH</span>}
                  </div>
                  <div className="text-[11px] text-[#434655] truncate" title={s.referrer || ''}>
                    {s.referrer ? new URL(s.referrer).hostname : 'no referrer'} · {s.first_path}
                  </div>
                  <div className="text-[10px] text-[#737686] truncate font-mono">{s.id.slice(0, 12)}…</div>
                </div>
                <div className="lg:col-span-1 flex flex-col justify-center">
                  {s.country_code ? (
                    <div className="flex items-center gap-1.5" data-testid={`session-country-${s.id}`}>
                      <span className="text-base leading-none">{FLAG(s.country_code)}</span>
                      <div className="min-w-0">
                        <div className="text-[12px] font-bold text-[#0b1c30] leading-tight">{s.country_code}</div>
                        {s.city && <div className="text-[10px] text-[#737686] truncate leading-tight" title={s.city}>{s.city}</div>}
                      </div>
                    </div>
                  ) : (
                    <span className="text-[10px] text-[#c3c6d7] italic">unknown</span>
                  )}
                </div>
                <div className="lg:col-span-2 text-[11px] text-[#434655]">
                  <div>{new Date(s.started_at).toLocaleString()}</div>
                  <div className="text-[10px] text-[#737686]">last: {new Date(s.last_seen_at).toLocaleTimeString()}</div>
                </div>
                <div className="lg:col-span-1 lg:text-right text-[12px] font-bold text-[#0b1c30]">{s.page_views}</div>
                <div className="lg:col-span-1 lg:text-right text-[12px] font-bold text-[#0b1c30]">{Math.round(s.max_scroll_pct || 0)}%</div>
                <div className="lg:col-span-1 lg:text-right text-[12px] font-bold text-[#0b1c30]">{Math.round((s.total_seconds || 0) / 60)}</div>
                <div className="lg:col-span-3 flex items-center gap-2 lg:justify-end">
                  <VerdictPill v={s.ai_verdict} />
                  <button
                    onClick={() => analyze(s.id)}
                    disabled={analyzing === s.id}
                    data-testid={`session-analyze-${s.id}`}
                    className="text-[10px] font-bold text-white bg-[#7c3aed] hover:bg-[#6d28d9] disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 rounded flex items-center gap-1 transition-all">
                    {analyzing === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {s.ai_verdict ? 'Re-analyze' : 'Analyze'}
                  </button>
                  <button
                    onClick={() => toggle(s.id)}
                    data-testid={`session-toggle-${s.id}`}
                    className="p-1 rounded text-[#434655] hover:bg-[#eff4ff]">
                    {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              {isOpen && s.ai_verdict && (
                <div className="px-4 pb-4 grid grid-cols-1 lg:grid-cols-3 gap-3 bg-gradient-to-br from-[#f8f9ff] to-white">
                  <div className="bg-white rounded-lg p-3 border border-[#c3c6d7]/30">
                    <div className="text-[9px] font-black uppercase tracking-widest text-[#7c3aed] mb-1">Persona · Engagement</div>
                    <div className="text-[13px] font-bold text-[#0b1c30]">{s.ai_verdict.persona}</div>
                    <div className="text-[10px] text-[#737686] uppercase mt-0.5">{s.ai_verdict.engagement}</div>
                    <div className="mt-2 grid grid-cols-3 gap-1.5 text-center">
                      <div><div className="text-base font-extrabold text-[#0b1c30]">{s.ai_verdict.intent_score}</div><div className="text-[9px] text-[#737686]">intent</div></div>
                      <div><div className="text-base font-extrabold text-emerald-700">{s.ai_verdict.will_buy_pct}%</div><div className="text-[9px] text-[#737686]">buy</div></div>
                      <div><div className="text-base font-extrabold text-[#004ac6]">{s.ai_verdict.will_return_pct}%</div><div className="text-[9px] text-[#737686]">return</div></div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-[#c3c6d7]/30">
                    <div className="text-[9px] font-black uppercase tracking-widest text-[#004ac6] mb-1">Verdict</div>
                    <div className="text-[12px] text-[#0b1c30] leading-relaxed">{s.ai_verdict.verdict}</div>
                    <div className="text-[9px] font-black uppercase tracking-widest text-emerald-700 mt-3 mb-1 flex items-center gap-1"><Target className="w-2.5 h-2.5" /> Next action</div>
                    <div className="text-[12px] text-[#0b1c30] leading-relaxed">{s.ai_verdict.next_action}</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-[#c3c6d7]/30">
                    <div className="text-[9px] font-black uppercase tracking-widest text-[#737686] mb-1 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5 text-amber-600" /> Signals</div>
                    <ul className="space-y-1 text-[11px] text-[#434655]">
                      {(s.ai_verdict.signals || []).map((sg, idx) => (
                        <li key={idx} className="flex gap-1.5"><span className="text-[#004ac6]">•</span><span>{sg}</span></li>
                      ))}
                    </ul>
                    {s.utm_campaign && (
                      <div className="mt-3 pt-2 border-t border-[#eff4ff] text-[10px] text-[#737686] flex items-center gap-1">
                        <ExternalLink className="w-2.5 h-2.5" /> Campaign: {s.utm_campaign}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
